import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Environment, Header } from '@aptsi-types';
import { EnvironmentService } from '../../services/environment.service';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { DeleteDialogComponent } from 'src/app/shared/dialogs/delete-dialog/delete-dialog.component';
import { WorkflowNavigationService } from '../../../shared/workflow-navigation.service';

type EnvironmentWithPagination = Environment & {
  totalEnvironments?: number;
  totalItems?: number;
  totalCount?: number;
  totalRecords?: number;
  totalPages?: number;
  currentPage?: number;
};

@Component({
  selector: 'app-environment-search',
  templateUrl: './environment-search.component.html',
  styleUrls: ['./environment-search.component.scss']
})
export class EnvironmentSearchComponent implements OnInit {
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;
  totalItemsNormal = 0;
  totalItemsSearch = 0;

  readonly itemsPerPage = 7;
  currentPage = 1;
  isAdd = false;
  isSearch = false;
  isLoading = true;
  isSearching = false;
  loadError = false;
  searchError = false;

  readonly sessionId: string;
  readonly userId: string;
  readonly transactionId: string;

  filteredData: Environment[] = [];
  searchedData: Environment[] = [];

  get displayedData(): Environment[] {
    return this.isSearch ? this.searchedData : this.filteredData;
  }

  readonly headers: Header[] = [
    {
      name: 'Environment ID',
      property: 'id',
      showInDropdown: true,
      description: 'Unique identifier for the environment'
    },
    {
      name: 'Environment Name',
      property: 'environmentName',
      showInDropdown: true,
      description: 'Name of the environment'
    },
    {
      name: 'Description',
      property: 'description',
      showInDropdown: false,
      description: 'Description of the environment'
    },
    {
      name: 'Last Updated',
      property: 'updatedAt',
      showInDropdown: true,
      description: 'Timestamp of the most recent update'
    }
  ];

  searchText = '';
  filterByHeader = 'all';
  nullver = 1;

  readonly buttonConfig = {
    update: true,
    delete: true,
    import: false,
    configure: false
  };

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly environmentService: EnvironmentService,
    private readonly dialog: MatDialog,
    keycloakStateService: KeycloakStateService,
    private readonly workflowNavigation: WorkflowNavigationService,
  ) {
    this.sessionId = keycloakStateService.getSessionId();
    this.userId = keycloakStateService.getUserId();
    this.transactionId = keycloakStateService.getTransactionId();
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.isAdd = params['isAdd'] === 'true';
    });

    void this.fetchEnvironments();
  }

  handleAction(event: { action: string; rowIndex: number; rowData: Environment }): void {
    if (event.action === 'update') {
      this.updateEnvironment(event.rowIndex);
    } else if (event.action === 'delete') {
      void this.deleteEnvironment(event.rowIndex);
    }
  }

  async fetchEnvironments(): Promise<void> {
    this.isLoading = true;
    this.loadError = false;

    try {
      const apiResponse = await this.environmentService
        .getEnvironments(this.currentPage - 1)
        .toPromise();
      const response: Environment[] = Array.isArray(apiResponse) ? apiResponse : [];
      const metadata = response[0] as EnvironmentWithPagination | undefined;

      this.filteredData = [...response];
      this.totalPagesNormal = this.readNonNegativeNumber(
        metadata?.totalPages,
        response.length > 0 ? 1 : 0
      );
      this.totalItemsNormal = this.readNonNegativeNumber(
        metadata?.totalEnvironments ?? metadata?.totalItems ?? metadata?.totalCount ?? metadata?.totalRecords,
        response.length
      );
      this.totalPages = this.totalPagesNormal;

      if (typeof metadata?.currentPage === 'number' && metadata.currentPage >= 0) {
        this.currentPage = metadata.currentPage + 1;
      } else if (this.totalPagesNormal === 0) {
        this.currentPage = 1;
      }
    } catch (error) {
      console.error('Error fetching environments:', error);
      this.loadError = true;
      this.filteredData = [];
      this.totalPagesNormal = 0;
      this.totalItemsNormal = 0;
      this.totalPages = 0;
    } finally {
      this.isLoading = false;
    }
  }

  async fetchEnvironmentsSearch(): Promise<void> {
    this.isSearching = true;
    this.searchError = false;

    try {
      const apiResponse = await this.environmentService
        .getEnvironmentsSearch(
          this.currentPage - 1,
          this.searchText.trim(),
          this.filterByHeader
        )
        .toPromise();

      const response: Environment[] = Array.isArray(apiResponse) ? apiResponse : [];
      const metadata = response[0] as EnvironmentWithPagination | undefined;
      this.searchedData = [...response];

      this.totalPagesSearch = this.readNonNegativeNumber(
        metadata?.totalPages,
        response.length > 0 ? 1 : 0
      );
      this.totalItemsSearch = this.readNonNegativeNumber(
        metadata?.totalEnvironments ?? metadata?.totalItems ?? metadata?.totalCount ?? metadata?.totalRecords,
        response.length
      );
      this.totalPages = this.totalPagesSearch;

      if (typeof metadata?.currentPage === 'number' && metadata.currentPage >= 0) {
        this.currentPage = metadata.currentPage + 1;
      }
    } catch (error) {
      console.error('Error searching environments:', error);
      this.searchError = true;
      this.searchedData = [];
      this.totalPagesSearch = 0;
      this.totalItemsSearch = 0;
      this.totalPages = 0;
    } finally {
      this.isSearching = false;
    }
  }

  async searchEnvironments(): Promise<void> {
    const query = this.searchText.trim();

    if (!query) {
      await this.clearSearch();
      return;
    }

    this.searchText = query;
    this.isSearch = true;
    this.currentPage = 1;
    await this.fetchEnvironmentsSearch();

    // Retain the existing client-side filtering behavior after the API request.
    const selectedHeader = this.headers.find(
      header => header.property === this.filterByHeader && header.showInDropdown
    );

    if (this.filterByHeader === 'all') {
      this.searchedData = this.searchedData.filter(environment =>
        this.headers.some(header => {
          const value = (environment as any)[header.property];
          return value?.toString().toLowerCase().includes(this.searchText.toLowerCase());
        })
      );
    } else if (selectedHeader) {
      this.searchedData = this.searchedData.filter(environment => {
        const value = (environment as any)[selectedHeader.property];
        return value?.toString().toLowerCase().includes(this.searchText.toLowerCase());
      });
      this.searchedData.sort((left, right) => {
        const leftValue = (left as any)[selectedHeader.property]?.toString() ?? '';
        const rightValue = (right as any)[selectedHeader.property]?.toString() ?? '';
        return leftValue.localeCompare(rightValue);
      });
    }
  }

  onInputChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
  }

  onFilterChange(): void {
    this.currentPage = 1;
    if (this.searchText.trim()) {
      this.isSearch = true;
      void this.searchEnvironments();
    } else {
      void this.clearSearch();
    }
  }

  async clearSearch(): Promise<void> {
    this.searchText = '';
    this.isSearch = false;
    this.searchError = false;
    this.currentPage = 1;
    this.totalPages = this.totalPagesNormal;
    await this.fetchEnvironments();
  }

  resetSearchText(): void {
    this.searchText = '';
    this.isSearch = false;
    this.currentPage = 1;
  }

  changePage(page: number): void {
    if (!Number.isFinite(page) || page < 1 || page === this.currentPage) {
      return;
    }

    this.currentPage = page;
    if (this.isSearch) {
      void this.fetchEnvironmentsSearch();
    } else {
      void this.fetchEnvironments();
    }
  }

    /**
     * Navigate to the environment creation form.
     */
    addEnvironment(): void {
      void this.workflowNavigation.navigateToWorkflow(
        'securityZoneManagement',
        ['environment', 'create']
      );
    }

  async deleteEnvironment(index: number): Promise<void> {
    try {
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        data: {
          title: 'Are you sure?',
          message: 'Do you really want to delete this Environment?',
          isConfirm: false
        }
      });

      const confirmed = await dialogRef.afterClosed().toPromise();
      if (!confirmed) return;

      const environmentId = this.displayedData[index]?.id;
      if (environmentId === null || environmentId === undefined || environmentId === '') return;

      await this.environmentService.deleteEnvironment(environmentId).toPromise();

      if (this.isSearch) {
        await this.fetchEnvironmentsSearch();
      } else {
        await this.fetchEnvironments();
      }

      this.dialog.open(DeleteDialogComponent, {
        data: {
          title: 'Success',
          message: 'The Environment has been deleted successfully.',
          isConfirm: true
        }
      });
    } catch (error) {
      console.error('Error deleting environment:', error);
    }
  }

/**
 * Navigate to the environment update form.
 */
    updateEnvironment(index: number): void {
      const environmentId = this.displayedData[index]?.id;

      if (
        environmentId === null ||
        environmentId === undefined ||
        environmentId === ''
      ) {
        return;
      }

      void this.workflowNavigation.navigateToWorkflow(
        'securityZoneManagement',
        ['environment', 'update', String(environmentId)]
      );
    }

    back(): void {
      if (this.isSearch) {
        void this.clearSearch();
        return;
      }

      void this.workflowNavigation.navigateToProductHome();
    }

  private readNonNegativeNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }
}
